import { AxiosInstance } from 'axios';
import { SWRConfiguration } from 'swr';

import { ArgWrapperT, WithStatusT } from '@consumer-frontend/nc-utils';

import {
  apiClient,
  authenticatedApplyApiClient,
  unauthenticatedApplyApiClient,
} from './axiosConfig';

// Deduping intervals for SWR requests. Default is 2 seconds.
export const ONE_MINUTE_IN_MS = 60 * 1000;
export const FIVE_MINUTES_IN_MS = 5 * ONE_MINUTE_IN_MS;

const returnDataWithStatus = <T>(res: { data: T; status: number; }): WithStatusT<T> => ({
  ...res.data,
  status: res.status,
});

export const createFetchers = (client: AxiosInstance) => ({
  delete: (url: string) => client.delete(url).then(returnDataWithStatus),
  get: (url: string) => client.get(url).then(returnDataWithStatus),
  getBlob: (url: string) => client.get(url, { responseType: 'blob' }).then((res) => res.data),
  post: <ArgT = unknown, RespT = unknown>(url: string, { arg }: ArgWrapperT<ArgT>) => (
    client.post<RespT>(url, arg).then(returnDataWithStatus)
  ),
  put: <ArgT = unknown, RespT = unknown>(url: string, { arg }: ArgWrapperT<ArgT>) => (
    client.put<RespT>(url, arg).then(returnDataWithStatus)
  ),
});

export type FetchersT = ReturnType<typeof createFetchers>;

// Authenticated fetchers — used by the dashboard app
export const dashboardFetchers = createFetchers(apiClient);

// Unauthenticated fetchers — used by the apply app for pre-auth endpoints
export const unauthenticatedFetchers = createFetchers(unauthenticatedApplyApiClient);

// Authenticated fetchers — used by the apply app for post-auth endpoints
export const applyFetchers = createFetchers(authenticatedApplyApiClient);

// See: https://swr.vercel.app/docs/api#options for available options and their defaults.
const defaultSWRConfig: SWRConfiguration = {
  onErrorRetry: (error, _key, _config, revalidate, { retryCount }) => {
    // Only retry up to 3 times.
    if (retryCount >= 3) { return error; }

    // Retry immediately.
    return revalidate({ retryCount });
  },
  revalidateOnFocus: false,
};

export const swrConfig: SWRConfiguration = {
  fetcher: dashboardFetchers.get,
  ...defaultSWRConfig,
};

export const applySwrConfig: SWRConfiguration = {
  ...defaultSWRConfig,
};
